/**
 * NPC — base class for non-player characters (Babylon.js port).
 *
 * Each NPC owns:
 *  - A capsule/sphere mesh for visual representation (placeholder until models land)
 *  - An NPCBehavior state machine
 *  - An optional calm-meter bar (GUI plane that follows the mesh)
 *  - Follow-target logic
 *
 * Subclasses customise appearance, behavior config, and special interactions.
 * Movement is applied directly to mesh.position (no Havok physics body) because
 * NPCs are kinematic — they don't need full rigid-body simulation.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { NPCBehavior, NPCBehaviorConfig } from "../../systems/NPCBehavior";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { COLORS } from "../../config/GameConfig";
import { getAssetManager, InstantiatedModel } from "../../systems/AssetManager";
import { setMeshVisibility } from "../../core/meshVisibility";

export interface NPCConfig {
  /** Unique identifier. */
  key: string;
  /** Spawn position (world units). */
  position: Vector3;
  /** Capsule height (world units, default 1.0). */
  height?: number;
  /** Capsule radius (world units, default 0.3). */
  radius?: number;
  /** Behaviour state-machine configuration. */
  behavior: NPCBehaviorConfig;
  /** Base diffuse colour hex string (e.g. '#c49b3c'). */
  color?: string;
  /**
   * Optional path to a GLB visual shell parented to the kinematic capsule.
   * When set, the capsule is hidden and the imported mesh becomes the
   * visible NPC. Load is fire-and-forget; gameplay does not block on it.
   */
  modelPath?: string;
  /** Uniform scale applied to the imported model root. */
  modelScale?: number;
  /** Local offset applied to the imported model root. */
  modelOffset?: Vector3;
  /** Y rotation applied to the imported model root (radians). */
  modelRotationY?: number;
}

export class NPC {
  public readonly npcKey: string;
  public readonly behavior: NPCBehavior;

  /**
   * Species identifier for per-species SFX routing (e.g. in AudioManager).
   * Subclasses override this to return their canonical species string.
   */
  get species(): string {
    return "npc";
  }

  /** Visual mesh (capsule). */
  public mesh: Mesh;
  protected scene: Scene;
  protected material: StandardMaterial;

  /** Calm-bar meshes (background + fill). */
  private calmBarBg: Mesh | null = null;
  private calmBarFill: Mesh | null = null;
  private calmBarFillMat: StandardMaterial | null = null;

  /** Imported GLB visual shell, if `modelPath` was provided. */
  protected visualModel: InstantiatedModel | null = null;

  private readonly capsuleHeight: number;

  constructor(scene: Scene, config: NPCConfig) {
    this.scene = scene;
    this.npcKey = config.key;
    this.behavior = new NPCBehavior(config.behavior);

    const height = config.height ?? 1.0;
    const radius = config.radius ?? 0.3;
    this.capsuleHeight = height;

    // Create capsule mesh
    this.mesh = MeshBuilder.CreateCapsule(
      `npc_${config.key}`,
      { height, radius, tessellation: 12, subdivisions: 4 },
      scene,
    );
    this.mesh.position = config.position.clone();

    // Material
    this.material = new StandardMaterial(`npc_${config.key}_mat`, scene);
    this.material.diffuseColor = config.color
      ? Color3.FromHexString(config.color)
      : Color3.FromHexString(COLORS.COPPER);
    this.material.specularColor = Color3.Black();
    this.mesh.material = this.material;

    // Optional GLB visual shell — loaded async and parented to the capsule.
    if (config.modelPath) {
      this.loadVisualModel(
        config.modelPath,
        config.modelScale ?? 1,
        config.modelOffset,
        config.modelRotationY,
      );
    }
  }

  /**
   * Fire-and-forget GLB loader. Hides the capsule on success so the
   * authored mesh becomes the visible NPC; the capsule remains for calm-bar
   * anchoring and kinematic position tracking.
   */
  private loadVisualModel(
    path: string,
    scale: number,
    offset: Vector3 | undefined,
    rotationY: number | undefined,
  ): void {
    getAssetManager()
      .instantiateModel(this.scene, path)
      .then((model) => {
        if (this.mesh.isDisposed()) {
          model.dispose();
          return;
        }
        this.visualModel = model;
        for (const root of model.rootNodes) {
          if (root instanceof TransformNode) {
            root.parent = this.mesh;
            root.position.copyFrom(offset ?? Vector3.Zero());
            root.scaling.setAll(scale);
            if (rotationY !== undefined) {
              root.rotation.y = rotationY;
            }
          }
        }
        for (const m of model.meshes) {
          m.isPickable = false;
          setMeshVisibility(m, this.mesh.visibility);
        }
        // Hide the capsule visual — keep it in the scene graph for
        // calm-bar parenting and position reads.
        this.mesh.isVisible = false;
      })
      .catch((err) => {
        console.warn("[NPC] Failed to load model", this.npcKey, path, err);
      });
  }

  /**
   * Update behavior with current purr state.
   * Called each frame by the scene/system that knows Minna's purr status.
   *
   * @param deltaSeconds  Frame delta in seconds.
   * @param purring       Whether Minna is currently using Calming Resonance.
   * @param purrX         Minna's world X while purring.
   * @param purrY         Minna's world Y while purring.
   */
  updateBehavior(deltaSeconds: number, purring: boolean, purrX: number, purrY: number): void {
    const prevState = this.behavior.state;
    const velocityX = this.behavior.update(
      deltaSeconds,
      this.mesh.position.x,
      this.mesh.position.y,
      purring,
      purrX,
      purrY,
    );

    // Apply horizontal velocity directly to mesh position (kinematic)
    this.mesh.position.x += velocityX * deltaSeconds;

    // Flip mesh to face movement direction (rotate around Y)
    if (velocityX < 0) {
      this.mesh.rotation.y = Math.PI;
    } else if (velocityX > 0) {
      this.mesh.rotation.y = 0;
    }

    // Emit state-change events
    if (prevState !== this.behavior.state) {
      gameEvents.emit(GameEvents.NPC_STATE_CHANGED, {
        npcKey: this.npcKey,
        prevState,
        newState: this.behavior.state,
      });

      if (this.behavior.state === 'calmed') {
        gameEvents.emit(GameEvents.NPC_CALMED, { npcKey: this.npcKey, species: this.species });
      }
    }

    // Update calm bar
    this.updateCalmBar();
  }

  /** Set a follow target (e.g., Minna after being calmed). */
  setFollowTarget(target: { x: number; y: number }): void {
    this.behavior.followTarget = target;
    this.behavior.setState('following');
  }

  /** Per-frame hook for subclass-specific visuals. Override in subclasses. */
  update(_time: number, _deltaSeconds: number): void {
    // Override in subclasses
  }

  /** Distance from this NPC to a world position (XY plane). */
  distanceTo(pos: Vector3): number {
    const dx = this.mesh.position.x - pos.x;
    const dy = this.mesh.position.y - pos.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  dispose(): void {
    this.calmBarBg?.dispose();
    this.calmBarFill?.dispose();
    this.calmBarFillMat?.dispose();
    this.visualModel?.dispose();
    this.visualModel = null;
    this.material.dispose();
    this.mesh.dispose();
  }

  protected syncVisualVisibility(value: number): void {
    this.mesh.visibility = value;
    if (!this.visualModel) return;
    for (const mesh of this.visualModel.meshes) {
      setMeshVisibility(mesh, value);
    }
  }

  // ── Calm bar ──────────────────────────────────────────────────

  private updateCalmBar(): void {
    const showBar =
      this.behavior.state === 'frantic' || this.behavior.state === 'calming';

    if (!showBar) {
      if (this.calmBarBg) this.calmBarBg.isVisible = false;
      if (this.calmBarFill) this.calmBarFill.isVisible = false;
      return;
    }

    // Lazy-create bar meshes
    if (!this.calmBarBg) {
      this.calmBarBg = MeshBuilder.CreatePlane(
        `npc_${this.npcKey}_calmBg`,
        { width: 1.4, height: 0.18 },
        this.scene,
      );
      this.calmBarBg.parent = this.mesh;
      this.calmBarBg.position = new Vector3(0, this.capsuleHeight * 0.6 + 0.3, -0.05);
      this.calmBarBg.billboardMode = Mesh.BILLBOARDMODE_ALL;
      const bgMat = new StandardMaterial(`npc_${this.npcKey}_calmBgMat`, this.scene);
      bgMat.diffuseColor = new Color3(0.2, 0.2, 0.2);
      bgMat.emissiveColor = new Color3(0.1, 0.1, 0.1);
      bgMat.alpha = 0.8;
      bgMat.specularColor = Color3.Black();
      this.calmBarBg.material = bgMat;

      this.calmBarFillMat = new StandardMaterial(
        `npc_${this.npcKey}_calmFillMat`,
        this.scene,
      );
      this.calmBarFillMat.diffuseColor = Color3.FromHexString(COLORS.EMERALD);
      this.calmBarFillMat.emissiveColor = Color3.FromHexString(COLORS.EMERALD).scale(0.3);
      this.calmBarFillMat.specularColor = Color3.Black();
      this.calmBarFillMat.alpha = 0.9;

      this.calmBarFill = MeshBuilder.CreatePlane(
        `npc_${this.npcKey}_calmFill`,
        { width: 1.4, height: 0.18 },
        this.scene,
      );
      this.calmBarFill.parent = this.mesh;
      this.calmBarFill.position = new Vector3(0, this.capsuleHeight * 0.6 + 0.3, -0.06);
      this.calmBarFill.billboardMode = Mesh.BILLBOARDMODE_ALL;
      this.calmBarFill.material = this.calmBarFillMat;
    }

    this.calmBarBg!.isVisible = true;
    this.calmBarFill!.isVisible = true;

    // Scale fill bar width by calm percent
    const pct = this.behavior.calmPercent;
    this.calmBarFill!.scaling.x = Math.max(0.001, pct);
    // Shift so the bar grows from left
    this.calmBarFill!.position.x = -(1.4 * (1 - pct)) / 2;
  }
}
